import React, { useState } from 'react';
import styled from 'styled-components';
import { useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { useGroups } from '../hooks/useGroups';
import { useAuth } from '../context/AuthContext';
import { useCurrentUserProfile } from '../hooks/useCurrentUserProfile';
import { hasAdminRole, hasRole } from '../utils/roles';
import { supabase } from '../services/supabase';

const Page = styled.div`
  background: var(--background);
  padding: 1.5rem;
  min-height: 100%;

  @media (max-width: 768px) {
    padding: 1rem;
  }
`;

const HeaderTitle = styled.h1`
  margin: 0 0 1.5rem 0;
  font-size: 1.8rem;
  color: var(--onSurface);
`;

const HeaderRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 1rem;
  margin-bottom: 1.5rem;
`;

const HeaderActions = styled.div`
  display: flex;
  align-items: center;
  gap: 0.75rem;
`;

const PrimaryButton = styled.button`
  border: none;
  border-radius: 999px;
  padding: 0.6rem 1.25rem;
  font-weight: 600;
  color: var(--surface);
  background: #1d4ed8;
  cursor: pointer;

  &:disabled {
    background: var(--onSurfaceVariant);
    cursor: not-allowed;
  }
`;

const SecondaryButton = styled.button`
  border: 1px solid #cbd5f5;
  border-radius: 999px;
  padding: 0.6rem 1.25rem;
  font-weight: 600;
  color: var(--onSurface);
  background: var(--surfaceVariant);
  cursor: pointer;
`;

const TinyButton = styled.button`
  border: 1px solid var(--outline);
  border-radius: 999px;
  background: var(--surface);
  color: var(--onSurface);
  font-size: 0.75rem;
  font-weight: 600;
  padding: 0.3rem 0.65rem;
  cursor: pointer;

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
`;

const FormCard = styled.div`
  background: var(--surface);
  border: 1px solid var(--outline);
  border-radius: 16px;
  padding: 1.25rem;
  margin-bottom: 1.5rem;
  box-shadow: 0 10px 20px rgba(15, 23, 42, 0.06);
`;

const FormGrid = styled.div`
  display: grid;
  gap: 1rem;
  grid-template-columns: repeat(auto-fit, minmax(220px, 1fr));
`;

const FormField = styled.label`
  display: flex;
  flex-direction: column;
  gap: 0.4rem;
  font-size: 0.85rem;
  color: var(--onSurfaceVariant);
`;

const Input = styled.input`
  padding: 0.65rem 0.75rem;
  border-radius: 10px;
  border: 1px solid var(--outline);
  font-size: 0.95rem;
  color: var(--onSurface);
  background: var(--surfaceVariant);
`;

const TextArea = styled.textarea`
  padding: 0.65rem 0.75rem;
  border-radius: 10px;
  border: 1px solid var(--outline);
  font-size: 0.95rem;
  color: var(--onSurface);
  background: var(--surfaceVariant);
  resize: vertical;
  min-height: 120px;
`;

const Select = styled.select`
  padding: 0.65rem 0.75rem;
  border-radius: 10px;
  border: 1px solid var(--outline);
  font-size: 0.95rem;
  color: var(--onSurface);
  background: var(--surfaceVariant);
`;

const ToggleRow = styled.div`
  display: flex;
  align-items: center;
  gap: 0.75rem;
  font-size: 0.9rem;
  color: #1f2937;
`;

const InlineActions = styled.div`
  display: flex;
  align-items: center;
  gap: 0.75rem;
  margin-top: 1rem;
`;

const InlineNameEditor = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  flex-wrap: wrap;
`;

const InlineFieldEditor = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.35rem;
  min-width: 140px;
`;

const TinyInput = styled(Input)`
  min-width: 220px;
`;

const TinySelect = styled(Select)`
  min-width: 140px;
  padding: 0.35rem 0.5rem;
  font-size: 0.85rem;
`;

const TinyNumberInput = styled(Input)`
  min-width: 90px;
  max-width: 110px;
  padding: 0.35rem 0.5rem;
  font-size: 0.85rem;
`;

const GROUP_CATEGORY_OPTIONS = [
  { value: 'life-stage', label: 'Life Stage' },
  { value: 'family', label: 'Family' },
  { value: 'seniors', label: 'Seniors' },
  { value: 'youth', label: 'Youth' },
  { value: 'ministry', label: 'Ministry' },
  { value: 'bible-study', label: 'Bible Study' },
  { value: 'prayer', label: 'Prayer' },
  { value: 'outreach', label: 'Outreach' },
  { value: 'other', label: 'Other' },
] as const;

const getCategoryLabel = (category?: string | null) => {
  if (!category) return '-';
  return GROUP_CATEGORY_OPTIONS.find((option) => option.value === category)?.label ?? category;
};

const TableCard = styled.div`
  background: var(--surface);
  border: 1px solid var(--outline);
  border-radius: 14px;
  box-shadow: 0 12px 24px rgba(17, 24, 39, 0.06);
  overflow: hidden;
`;

const TableWrap = styled.div`
  overflow-x: auto;
`;

const Table = styled.table`
  width: 100%;
  border-collapse: collapse;

  th,
  td {
    padding: 0.75rem 1rem;
    border-bottom: 1px solid var(--outline);
    text-align: left;
    font-size: 0.9rem;
    color: var(--onSurfaceVariant);
  }

  th {
    font-size: 0.75rem;
    text-transform: uppercase;
    letter-spacing: 0.04em;
    color: var(--onSurfaceVariant);
    background: var(--surfaceVariant);
  }
`;

const ActionButtons = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  flex-wrap: wrap;
`;

const HelperText = styled.p<{ $tone?: 'error' | 'success' }>`
  margin: 0.5rem 0 0;
  font-size: 0.85rem;
  color: ${({ $tone }) => ($tone === 'error' ? 'var(--error)' : '#047857')};
`;

export const GroupsPage: React.FC = () => {
  const { data, isLoading, error } = useGroups();
  const { user } = useAuth();
  const { data: profile } = useCurrentUserProfile(user?.id);
  const roles = profile?.roles ?? user?.user_metadata?.roles;
  const canCreateGroups = hasAdminRole(roles) || hasRole(roles, 'leader');
  const canRenameGroups = hasAdminRole(roles);
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const [isCreateFormOpen, setIsCreateFormOpen] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [category, setCategory] = useState('other');
  const [isPrivate, setIsPrivate] = useState(false);
  const [maxMembers, setMaxMembers] = useState('');
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [submitSuccess, setSubmitSuccess] = useState<string | null>(null);
  const [editingGroupId, setEditingGroupId] = useState<string | null>(null);
  const [editingName, setEditingName] = useState('');
  const [editingCategory, setEditingCategory] = useState('other');
  const [editingMaxMembers, setEditingMaxMembers] = useState('');
  const [isRenaming, setIsRenaming] = useState(false);
  const [renameError, setRenameError] = useState<string | null>(null);

  const startRename = (
    groupId: string,
    currentName: string,
    currentCategory?: string | null,
    currentMaxMembers?: number | null
  ) => {
    setEditingGroupId(groupId);
    setEditingName(currentName);
    setEditingCategory(currentCategory || 'other');
    setEditingMaxMembers(
      currentMaxMembers != null && Number.isFinite(currentMaxMembers) ? String(currentMaxMembers) : ''
    );
    setRenameError(null);
    setSubmitSuccess(null);
  };

  const cancelRename = () => {
    setEditingGroupId(null);
    setEditingName('');
    setEditingCategory('other');
    setEditingMaxMembers('');
    setRenameError(null);
  };

  const handleRenameGroup = async (groupId: string) => {
    if (!canRenameGroups) return;
    const trimmedName = editingName.trim();
    if (!trimmedName) {
      setRenameError('Group name cannot be empty.');
      return;
    }
    if (!GROUP_CATEGORY_OPTIONS.some((option) => option.value === editingCategory)) {
      setRenameError('Select a valid category.');
      return;
    }

    const trimmedMaxMembers = editingMaxMembers.trim();
    const parsedMaxMembers = trimmedMaxMembers ? Number(trimmedMaxMembers) : null;
    if (
      parsedMaxMembers !== null &&
      (!Number.isFinite(parsedMaxMembers) || !Number.isInteger(parsedMaxMembers) || parsedMaxMembers <= 0)
    ) {
      setRenameError('Max members must be a positive whole number or empty.');
      return;
    }

    setIsRenaming(true);
    setRenameError(null);

    const { data, error: invokeError } = await supabase.functions.invoke('rename-group', {
      body: {
        group_id: groupId,
        name: trimmedName,
        category: editingCategory,
        max_members: parsedMaxMembers,
      },
    });

    if (invokeError || data?.error) {
      setRenameError(invokeError?.message || data?.error || 'Failed to update group.');
      setIsRenaming(false);
      return;
    }

    setSubmitSuccess('Group updated successfully.');
    setIsRenaming(false);
    cancelRename();
    queryClient.invalidateQueries({ queryKey: ['groups'] });
  };

  const handleCreateGroup = async (event: React.FormEvent) => {
    event.preventDefault();
    if (!user) {
      setSubmitError('You must be signed in to create a group.');
      setSubmitSuccess(null);
      return;
    }
    if (!name.trim() || !description.trim()) {
      setSubmitError('Group name and description are required.');
      setSubmitSuccess(null);
      return;
    }

    const parsedMaxMembers = maxMembers.trim() ? Number(maxMembers) : undefined;
    if (parsedMaxMembers !== undefined && (!Number.isFinite(parsedMaxMembers) || parsedMaxMembers <= 0)) {
      setSubmitError('Max members must be a positive number.');
      setSubmitSuccess(null);
      return;
    }

    setSubmitError(null);
    setSubmitSuccess(null);
    setIsSubmitting(true);

    const defaultSettings = {
      allowMemberPosts: true,
      requireApproval: false,
      allowMediaSharing: true,
      allowChatCreation: true,
      autoApproveMembers: false,
      attireEnabled: false,
    };

    const { error: insertError } = await supabase
      .from('groups')
      .insert({
        name: name.trim(),
        description: description.trim(),
        category,
        is_private: isPrivate,
        max_members: parsedMaxMembers,
        created_by: user.id,
        admin_ids: [user.id],
        current_members: 0,
        settings: defaultSettings,
        created_at: new Date().toISOString(),
        updated_at: new Date().toISOString(),
      })
      .select()
      .single();

    if (insertError) {
      setSubmitError(insertError.message || 'Failed to create group.');
      setSubmitSuccess(null);
      setIsSubmitting(false);
      return;
    }

    setSubmitSuccess('Group created successfully.');
    setSubmitError(null);
    setName('');
    setDescription('');
    setCategory('other');
    setIsPrivate(false);
    setMaxMembers('');
    setIsSubmitting(false);
    queryClient.invalidateQueries({ queryKey: ['groups'] });
  };

  if (isLoading) return <p>Loading groups…</p>;
  if (error) return <p>Error loading groups</p>;

  return (
    <Page>
      <HeaderRow>
        <HeaderTitle>Groups</HeaderTitle>
        {canCreateGroups ? (
          <HeaderActions>
            <PrimaryButton type="button" onClick={() => setIsCreateFormOpen((prev) => !prev)}>
              {isCreateFormOpen ? 'Close' : 'Create Group'}
            </PrimaryButton>
          </HeaderActions>
        ) : null}
      </HeaderRow>

      {canCreateGroups && isCreateFormOpen ? (
        <FormCard>
          <form onSubmit={handleCreateGroup}>
            <FormGrid>
              <FormField>
                Group Name *
                <Input
                  value={name}
                  onChange={(event) => setName(event.target.value)}
                  placeholder="e.g. Men's Bible Study"
                  maxLength={255}
                />
              </FormField>
              <FormField>
                Category
                <Select value={category} onChange={(event) => setCategory(event.target.value)}>
                  {GROUP_CATEGORY_OPTIONS.map((option) => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </Select>
              </FormField>
              <FormField>
                Max Members (optional)
                <Input
                  value={maxMembers}
                  onChange={(event) => setMaxMembers(event.target.value)}
                  placeholder="e.g. 25"
                  inputMode="numeric"
                />
              </FormField>
              <FormField>
                Privacy
                <ToggleRow>
                  <input
                    type="checkbox"
                    checked={isPrivate}
                    onChange={(event) => setIsPrivate(event.target.checked)}
                  />
                  {isPrivate ? 'Private' : 'Public'}
                </ToggleRow>
              </FormField>
            </FormGrid>
            <FormField>
              Description *
              <TextArea
                value={description}
                onChange={(event) => setDescription(event.target.value)}
                placeholder="Share the purpose of this group..."
                maxLength={1000}
              />
            </FormField>
            <InlineActions>
              <PrimaryButton type="submit" disabled={isSubmitting}>
                {isSubmitting ? 'Creating...' : 'Create Group'}
              </PrimaryButton>
              <SecondaryButton
                type="button"
                onClick={() => {
                  setIsCreateFormOpen(false);
                  setSubmitError(null);
                  setSubmitSuccess(null);
                }}
              >
                Cancel
              </SecondaryButton>
            </InlineActions>
            {submitError ? <HelperText $tone="error">{submitError}</HelperText> : null}
            {submitSuccess ? <HelperText $tone="success">{submitSuccess}</HelperText> : null}
          </form>
        </FormCard>
      ) : null}

      <TableCard>
        <TableWrap>
          <Table>
            <thead>
              <tr>
                <th>Name</th>
                <th>Admin(s)</th>
                <th>Category</th>
                <th>Members</th>
                <th>Posts</th>
                <th>Media</th>
                <th>Docs</th>
                <th>Privacy</th>
                <th>Status</th>
                {canRenameGroups ? <th>Actions</th> : null}
              </tr>
            </thead>
            <tbody>
              {data?.map((g) => (
                <tr key={g.id}>
                  <td>
                    {canRenameGroups && editingGroupId === g.id ? (
                      <InlineNameEditor>
                        <TinyInput
                          value={editingName}
                          onChange={(event) => setEditingName(event.target.value)}
                          maxLength={255}
                          autoFocus
                        />
                      </InlineNameEditor>
                    ) : (
                      <InlineNameEditor>
                        <span>{g.name}</span>
                        {canRenameGroups ? (
                          <TinyButton
                            type="button"
                            onClick={() => startRename(g.id, g.name, g.category, g.max_members)}
                          >
                            Edit
                          </TinyButton>
                        ) : null}
                      </InlineNameEditor>
                    )}
                  </td>
                  <td>{g.admin_names?.length ? g.admin_names.join(', ') : '-'}</td>
                  <td>
                    {canRenameGroups && editingGroupId === g.id ? (
                      <InlineFieldEditor>
                        <TinySelect
                          value={editingCategory}
                          onChange={(event) => setEditingCategory(event.target.value)}
                          disabled={isRenaming}
                        >
                          {GROUP_CATEGORY_OPTIONS.map((option) => (
                            <option key={option.value} value={option.value}>
                              {option.label}
                            </option>
                          ))}
                        </TinySelect>
                      </InlineFieldEditor>
                    ) : (
                      getCategoryLabel(g.category)
                    )}
                  </td>
                  <td>
                    {canRenameGroups && editingGroupId === g.id ? (
                      <InlineFieldEditor>
                        <InlineNameEditor>
                          <span>{g.current_members ?? 0} /</span>
                          <TinyNumberInput
                            value={editingMaxMembers}
                            onChange={(event) => setEditingMaxMembers(event.target.value)}
                            placeholder="Max"
                            inputMode="numeric"
                            disabled={isRenaming}
                            aria-label="Max members"
                          />
                        </InlineNameEditor>
                      </InlineFieldEditor>
                    ) : (
                      `${g.current_members ?? 0}/${g.max_members ?? '-'}`
                    )}
                  </td>
                  <td>{g.posts_count ?? 0}</td>
                  <td>{g.media_count ?? 0}</td>
                  <td>{g.docs_count ?? 0}</td>
                  <td>{g.is_private ? 'Private' : 'Public'}</td>
                  <td>{g.status ?? '-'}</td>
                  {canRenameGroups ? (
                    <td>
                      <ActionButtons>
                        {editingGroupId === g.id ? (
                          <>
                            <TinyButton
                              type="button"
                              disabled={isRenaming}
                              onClick={() => handleRenameGroup(g.id)}
                            >
                              {isRenaming ? 'Saving…' : 'Save'}
                            </TinyButton>
                            <TinyButton type="button" disabled={isRenaming} onClick={cancelRename}>
                              Cancel
                            </TinyButton>
                          </>
                        ) : (
                          <TinyButton type="button" onClick={() => navigate(`/groups/${g.id}/manage`)}>
                            Manage
                          </TinyButton>
                        )}
                      </ActionButtons>
                    </td>
                  ) : null}
                </tr>
              ))}
            </tbody>
          </Table>
        </TableWrap>
      </TableCard>
      {renameError ? <HelperText $tone="error">{renameError}</HelperText> : null}
      {submitSuccess ? <HelperText $tone="success">{submitSuccess}</HelperText> : null}
    </Page>
  );
};

export default GroupsPage;
